import React, { useRef, useState, useLayoutEffect } from "react";
import { motion } from "framer-motion";
import { NG_STATES, NG_VIEWBOX } from "@/data/nigeriaStates";

const SHADES = ["#0f7a4f", "#0a5230", "#12925d", "#0c6b45"];
const fixName = (n) => (n === "Nassarawa" ? "Nasarawa" : n === "Federal Capital Territory" ? "FCT (Abuja)" : n);

// variant: "faint" (background motif) | "feature" (visible, states marked)
export const NigeriaMap = ({ className = "", variant = "faint", animate = true }) => {
  const fctRef = useRef(null);
  const [fct, setFct] = useState(null);
  const [selected, setSelected] = useState(null); // { name, x, y }

  useLayoutEffect(() => {
    if (fctRef.current) {
      const b = fctRef.current.getBBox();
      setFct({ x: b.x + b.width / 2, y: b.y + b.height / 2 });
    }
  }, []);

  const feature = variant === "feature";

  const handleSelect = (e, name) => {
    const b = e.currentTarget.getBBox();
    setSelected({ name: fixName(name), x: b.x + b.width / 2, y: b.y + b.height / 2 });
  };

  return (
    <svg
      viewBox={NG_VIEWBOX}
      className={className}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden={feature ? undefined : "true"}
      aria-label={feature ? "Interactive map of Nigeria — 36 states and the FCT" : undefined}
      role={feature ? "img" : undefined}
      style={feature ? { filter: "drop-shadow(0 0 26px rgba(0,224,122,0.2))" } : undefined}
    >
      <motion.g
        initial={animate ? { opacity: 0 } : false}
        whileInView={animate ? { opacity: 1 } : {}}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
      >
        {NG_STATES.map((st, i) => {
          const isFct = /federal|abuja/i.test(st.name);
          if (feature) {
            const isSel = selected && selected.name === fixName(st.name);
            return (
              <path
                key={st.id}
                ref={isFct ? fctRef : undefined}
                d={st.d}
                className="ng-state"
                onClick={(e) => handleSelect(e, st.name)}
                fill={isSel ? "#4bf0a6" : isFct ? "#1fae70" : SHADES[i % SHADES.length]}
                stroke="rgba(255,255,255,0.32)"
                strokeWidth="0.8"
              >
                <title>{fixName(st.name)}</title>
              </path>
            );
          }
          return (
            <path
              key={st.id}
              ref={isFct ? fctRef : undefined}
              d={st.d}
              fill="rgba(255,255,255,0.045)"
              stroke="rgba(255,255,255,0.12)"
              strokeWidth="0.6"
            />
          );
        })}
      </motion.g>

      {feature && fct && (
        <g>
          <motion.circle
            cx={fct.x}
            cy={fct.y}
            r="11"
            fill="none"
            stroke="#D6FF00"
            strokeWidth="1"
            initial={{ opacity: 0.7, scale: 0.4 }}
            animate={{ opacity: 0, scale: 2 }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }}
            style={{ transformOrigin: `${fct.x}px ${fct.y}px` }}
          />
          <circle cx={fct.x} cy={fct.y} r="4.5" fill="#D6FF00" stroke="#08301f" strokeWidth="1" />
          <text
            x={fct.x + 10}
            y={fct.y + 4}
            fill="#D6FF00"
            fontSize="15"
            fontFamily="'JetBrains Mono', monospace"
            fontWeight="600"
          >
            Abuja · FCT
          </text>
        </g>
      )}

      {feature && selected && (
        <g style={{ pointerEvents: "none" }}>
          <rect
            x={selected.x - (selected.name.length * 4.7 + 12)}
            y={selected.y - 36}
            width={selected.name.length * 9.4 + 24}
            height="26"
            rx="3"
            fill="#0a0a0b"
            stroke="#D6FF00"
            strokeWidth="1"
          />
          <text
            x={selected.x}
            y={selected.y - 18}
            textAnchor="middle"
            fill="#D6FF00"
            fontSize="15"
            fontFamily="'JetBrains Mono', monospace"
            fontWeight="600"
          >
            {selected.name}
          </text>
          <circle cx={selected.x} cy={selected.y} r="4.5" fill="#D6FF00" stroke="#08301f" strokeWidth="1" />
        </g>
      )}
    </svg>
  );
};
